import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import "./DataAccuracy.css";
import {
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  Typography,
  Chip,
  Tooltip,
  Avatar,
  AvatarGroup,
  Link,
} from "@mui/material";
import React, { useEffect } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import DataAccuracyFilterCard from "../DataAccuracyFilter/DataAccuracyFilterCard";
import {
  setPage,
  setRowsPerPage,
  setSelectedRows,
  setSort,
} from "../../../../../features/dataAccuracySlice";
import { useSelector, useDispatch } from "react-redux";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import DragHandleOutlinedIcon from "@mui/icons-material/DragHandleOutlined";
import { Box } from "@mui/system";
import { deduplicateTasks } from "../DataAccuracyHelpers/helpers";
import {
  selectAllUsers,
  selectUserData,
} from "../../../../../features/userSlice";
import { formatDateToDDMMYY } from "../../../../../utils/helpers";
import DataAccuracyGraph from "./DataAccuracyGraph";

function DataAccuracyTable() {
  const {
    page,
    rowsPerPage,
    selectedRows,
    sort,
    selectedDataAccuracy,
    selectedPriority,
    selectedCategory,
    selectedAssignee,
    selectedOwner,
    selectedBrand,
    lastUpdatedDate,
    dataAccruracyData,
    displayMode,
    selectedAssigneeTasks,
  } = useSelector((state: any) => state.dataAccuracy);
  const dispatch = useDispatch();
  const allUsers = useSelector(selectAllUsers);
  const deduplicatedData = React.useMemo(() => {
    const rawData = dataAccruracyData?.query_output || [];
    if (!rawData.length) return [];

    const filteredRaw = rawData.filter((item: any) => {
      // 1. Data Accuracy Title Filter
      if (
        selectedDataAccuracy &&
        selectedDataAccuracy.length !== 0 &&
        !selectedDataAccuracy.includes(item?.title)
      ) {
        return false;
      }

      // 2. Priority Filter
      if (
        selectedPriority &&
        selectedPriority.length !== 0 &&
        !selectedPriority.includes(item?.priority)
      ) {
        return false;
      }

      // 3. Category Filter
      if (
        selectedCategory &&
        selectedCategory.length !== 0 &&
        !selectedCategory.includes(item?.category)
      ) {
        return false;
      }

      // 4. Owner Filter
      if (selectedOwner && selectedOwner.length !== 0) {
        const ownerId = item?.owner;
        const ownerUser = allUsers.find(
          (u: any) => String(u.id) === String(ownerId),
        );
        const ownerName = ownerUser
          ? `${ownerUser.first_name || ""} ${ownerUser.last_name || ""}`.trim()
          : "";
        if (!selectedOwner.includes(ownerName)) return false;
      }

      // 5. Assignee Filter
      if (selectedAssigneeTasks && selectedAssigneeTasks.length !== 0) {
        const assigneeId = item?.assignee;
        const assigneeUser = allUsers.find(
          (u: any) => String(u.id) === String(assigneeId),
        );
        const assigneeName = assigneeUser
          ? `${assigneeUser.first_name || ""} ${assigneeUser.last_name || ""}`.trim()
          : "";
        if (!selectedAssigneeTasks.includes(assigneeName)) return false;
      }

      // 6. Date Range Filter
      if (
        lastUpdatedDate &&
        lastUpdatedDate.length === 2 &&
        lastUpdatedDate[0] &&
        lastUpdatedDate[1]
      ) {
        const itemDate = new Date(item?.last_updated);
        const startDate = new Date(lastUpdatedDate[0]);
        const endDate = new Date(
          new Date(lastUpdatedDate[1]).setHours(23, 59, 59, 999),
        );

        if (lastUpdatedDate[0] === lastUpdatedDate[1]) {
          if (
            new Date(item?.created_at).toDateString() !==
            startDate.toDateString()
          ) {
            return false;
          }
        } else if (itemDate < startDate || itemDate > endDate) {
          return false;
        }
      }

      return true;
    });

    return deduplicateTasks(filteredRaw, allUsers);
  }, [
    dataAccruracyData,
    allUsers,
    selectedDataAccuracy,
    selectedPriority,
    selectedCategory,
    selectedOwner,
    selectedAssigneeTasks,
    lastUpdatedDate,
  ]);

  const [sortedData, setSortedData] = React.useState<any[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = React.useState(true);

  const response = useSelector(selectUserData);
  const navigate = useNavigate();
  const location = useLocation();
  const hideDone = searchParams.get("doneIssues") === "hideDone";

  const handleChangePage = (_: unknown, newPage: number) => {
    dispatch(setPage(newPage));

    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("page", newPage.toString());
      return updatedParams;
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setRowsPerPage(+event.target.value));
    dispatch(setPage(0));
  };

  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) return arrayToSort;

      return arrayToSort.slice().sort((a, b) => {
        if (sort.keytoSort === "priority") {
          const priorityOrder = {
            highest: 5,
            high: 4,
            medium: 3,
            low: 2,
            lowest: 1,
          } as any;

          const aPriority = priorityOrder[a.priority?.toLowerCase()] || 0;
          const bPriority = priorityOrder[b.priority?.toLowerCase()] || 0;

          return sort.direction === "asc"
            ? aPriority - bPriority
            : bPriority - aPriority;
        }

        let aValue = a[sort.keytoSort];
        let bValue = b[sort.keytoSort];

        if (sort.keytoSort === "open_issues") {
          aValue =
            (a.in_progress || 0) +
            (a.to_do || 0) +
            (a.in_review || 0) +
            (a.snooze || 0);
          bValue =
            (b.in_progress || 0) +
            (b.to_do || 0) +
            (b.in_review || 0) +
            (b.snooze || 0);
        } else if (sort.keytoSort === "owner") {
          aValue = Number(a.owner);
          bValue = Number(b.owner);
        } else {
          aValue = a[sort.keytoSort];
          bValue = b[sort.keytoSort];
        }

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        }

        return sort.direction === "asc"
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      });
    },
    [sort],
  );

  React.useEffect(() => {
    if (deduplicatedData.length > 0 || dataAccruracyData?.query_output) {
      const sortedRows = getSortedData(deduplicatedData);
      setSortedData(sortedRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deduplicatedData, getSortedData]);

  const filteredData = React.useMemo(
    () =>
      sortedData.filter((dataAccuracy: any) => {
        // Calculate open issues count for this row
        const openIssuesCount =
          (dataAccuracy.in_progress || 0) +
          (dataAccuracy.to_do || 0) +
          (dataAccuracy.in_review || 0);

        // Hide rows only when all occurrences are Done or Snoozed (no open issues)
        if (hideDone && openIssuesCount === 0) return false;
        // if (hideDone && dataAccuracy?.snooze > 0) return false;

        return true;
      }),
    [sortedData, hideDone],
  );

  const tableheaders = [
    {
      id: 1,
      KEY: "title",
      label: "Discrepancy Type",
      sortable: true,
      align: "left",
    },
    {
      id: 2,
      KEY: "priority",
      label: "Priority",
      sortable: true,
      align: "center",
    },
    {
      id: 3,
      KEY: "open_issues",
      label: "Open Issues",
      sortable: true,
      align: "center",
    },
    ...(displayMode === "Extended"
      ? [
          {
            id: 4,
            KEY: "to_do",
            label: "To Do",
            sortable: true,
            align: "center",
          },
          {
            id: 5,
            KEY: "in_progress",
            label: "In Progress",
            sortable: true,
            align: "center",
          },
          {
            id: 6,
            KEY: "in_review",
            label: "Review",
            sortable: true,
            align: "center",
          },
          {
            id: 7,
            KEY: "snooze",
            label: "Snoozed",
            sortable: true,
            align: "center",
          },
          {
            id: 8,
            KEY: "done",
            label: "Done",
            sortable: true,
            align: "center",
          },
        ]
      : []),
    {
      id: 9,
      KEY: "category",
      label: "Category",
      sortable: true,
      align: "left",
    },
    { id: 10, KEY: "website", label: "Assets", sortable: true, align: "left" },
    {
      id: 11,
      KEY: "last_updated",
      label: "Last Updated",
      sortable: true,
      align: "left",
    },
    { id: 12, KEY: "owner", label: "Owner", sortable: true, align: "left" },
    {
      id: 13,
      KEY: "assignee",
      label: "Assignee",
      sortable: false,
      align: "left",
    },
  ];

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return;

    dispatch(
      setSort({
        keytoSort: header.KEY,
        direction:
          header.KEY === sort.keytoSort && sort.direction === "asc"
            ? "desc"
            : "asc",
      }),
    );
  };

  useEffect(() => {
    const currentPage = searchParams.get("page");

    if (currentPage !== null) {
      dispatch(setPage(Number(currentPage)));
    } else {
      dispatch(setPage(0));
      setSearchParams((prevParams) => {
        const updatedParams = new URLSearchParams(prevParams);
        updatedParams.set("page", "0");
        return updatedParams;
      });
    }
    // eslint-disable-next-line
  }, [
    selectedPriority,
    selectedAssignee,
    selectedBrand,
    lastUpdatedDate,
    selectedOwner,
    selectedCategory,
    selectedDataAccuracy,
    searchParams,
  ]);

  useEffect(() => {
    dispatch(setSelectedRows([]));

    const params = new URLSearchParams(location.search);
    let changed = false;

    if (params.has("task_structure_shared_uid")) {
      params.delete("task_structure_shared_uid");
      changed = true;
    }

    if (params.has("asset_shared_uids")) {
      params.delete("asset_shared_uids");
      changed = true;
    }

    if (changed) {
      navigate(
        {
          pathname: location.pathname,
          search: params.toString(),
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line
  }, []);

  // Helper function to construct the URL for each row
  const getTaskUrl = (row: any) => {
    const currentParams = new URLSearchParams(window.location.search);
    const matchedSharedUids: string[] = [];

    response?.data?.website_permissions.forEach((siteObj: any) => {
      const [sharedUid, siteData] = Object.entries(siteObj)[0] as any;

      if (row?.websites.includes(siteData.ui_site_name)) {
        matchedSharedUids.push(sharedUid);
      }
    });

    currentParams.set(
      "task_structure_shared_uid",
      row?.task_structure_shared_uid,
    );
    currentParams.set("page", "0");

    if (matchedSharedUids.length > 0) {
      currentParams.set("asset_shared_uids", matchedSharedUids.join(","));
    }

    return `/operations/data_accuracy/task_name?${currentParams.toString()}`;
  };

  const handleClickEdit = (row: any) => {
    navigate(getTaskUrl(row));
  };

  useEffect(() => {
    if (dataAccruracyData?.query_output) {
      setIsLoading(false);
      const sortedRows = getSortedData(deduplicatedData);
      setSortedData(sortedRows);
    } else {
      setIsLoading(true);
    }
    // eslint-disable-next-line
  }, []);

  const totalCount = filteredData.reduce(
    (sum, row) =>
      sum + (row.in_progress || 0) + (row.to_do || 0) + (row.in_review || 0),
    0,
  );

  // Helper function to render priority icon
  const renderPriorityIcon = (priority: string) => {
    const priorityLower = priority?.toLowerCase();

    if (priorityLower === "highest") {
      return <KeyboardDoubleArrowUpIcon sx={{ color: "#D32F2F" }} />;
    } else if (priorityLower === "high") {
      return <KeyboardArrowUpIcon sx={{ color: "#D32F2F" }} />;
    } else if (priorityLower === "medium") {
      return <DragHandleOutlinedIcon sx={{ color: "#ED6C02" }} />;
    } else if (priorityLower === "low") {
      return <KeyboardArrowDownIcon sx={{ color: "#2196F3" }} />;
    } else if (priorityLower === "lowest") {
      return <KeyboardDoubleArrowDownIcon sx={{ color: "#2196F3" }} />;
    }
    return null;
  };

  return (
    <>
      <DataAccuracyFilterCard />
      <DataAccuracyGraph />
      <TableContainer
        className="DataAccuracyWrap"
        component={Paper}
        sx={{ boxShadow: "none", maxHeight: 500 }}
      >
        <Table aria-label="sticky table" size="small" stickyHeader>
          <TableHead sx={{ zIndex: 99 }}>
            <TableRow>
              {tableheaders.map((header) => (
                <TableCell
                  key={header.id}
                  align={header.align as any}
                  sx={{
                    verticalAlign: "middle",
                    padding: "8px 12px",
                  }}
                >
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  <Typography variant="body1" color="textSecondary">
                    Loading...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredData?.length > 0 ? (
              <>
                {filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: any, index: any) => {
                    const openIssuesCount =
                      Number(row?.to_do) +
                      Number(row?.in_progress) +
                      Number(row?.in_review);
                    const toDoCount = Number(row?.to_do);

                    return (
                      <TableRow
                        key={index}
                        selected={selectedRows.includes(index)}
                      >
                        <TableCell align="left">
                          <Link
                            href={getTaskUrl(row)}
                            onClick={(e) => {
                              // Only prevent default for normal left-click without modifiers
                              if (
                                e.button === 0 &&
                                !e.ctrlKey &&
                                !e.metaKey &&
                                !e.shiftKey
                              ) {
                                e.preventDefault();
                                handleClickEdit(row);
                              }
                              // For Ctrl+Click, Cmd+Click, middle-click, etc., let browser handle naturally
                            }}
                            sx={{
                              textDecoration: "underline",
                              color: "#9C27B0",
                              cursor: "pointer",
                              "&:hover": {
                                color: "#7B1FA2",
                              },
                            }}
                            data-tracker="173"
                          >
                            {row.title}
                          </Link>
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {renderPriorityIcon(row?.priority)}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {displayMode === "Classic" ? (
                            <Tooltip
                              title={`To Do: ${toDoCount}, In Progress: ${Number(
                                row?.in_progress,
                              )}, In Review: ${Number(row?.in_review)}`}
                              arrow
                              placement="top"
                              disableInteractive
                            >
                              <Box
                                sx={{
                                  fontWeight:
                                    openIssuesCount > 10 ? "bold" : "normal",
                                  color:
                                    openIssuesCount > 10
                                      ? "#D32F2F"
                                      : "inherit",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  textAlign: "center",
                                }}
                              >
                                {openIssuesCount}
                              </Box>
                            </Tooltip>
                          ) : (
                            <Box
                              sx={{
                                fontWeight:
                                  openIssuesCount > 10 ? "bold" : "normal",
                                color:
                                  openIssuesCount > 10 ? "#D32F2F" : "inherit",

                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                              }}
                            >
                              {openIssuesCount}
                            </Box>
                          )}
                        </TableCell>

                        {displayMode === "Extended" && (
                          <>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  fontWeight:
                                    toDoCount > 10 ? "bold" : "normal",
                                  color: toDoCount > 10 ? "#D32F2F" : "inherit",
                                  textAlign: "center",
                                }}
                              >
                                {toDoCount}
                              </Box>
                            </TableCell>

                            <TableCell align="center">
                              {Number(row?.in_progress)}
                            </TableCell>
                            <TableCell align="center">
                              {Number(row?.in_review)}
                            </TableCell>
                            <TableCell align="center">
                              {Number(row?.snooze)}
                            </TableCell>
                            <TableCell align="center">
                              {Number(row?.done)}
                            </TableCell>
                          </>
                        )}
                        <TableCell align="left">{row?.category}</TableCell>
                        <TableCell align="left">
                          <div className="chip-data">
                            {(() => {
                              const siteEntries = Object.values(row.websites)
                                .filter((site: any) => site)
                                .map((site: any) => site);
                              const mainSite = siteEntries[0] || "";
                              const additionalCount = siteEntries.length - 1;
                              return (
                                <>
                                  <Chip
                                    label={
                                      additionalCount > 0
                                        ? `${mainSite}`
                                        : mainSite
                                    }
                                    sx={{
                                      backgroundColor: "#7B1FA2",
                                      color: "#ffffff",
                                      height: "26px",
                                      zIndex: "1",
                                    }}
                                  />
                                  <AvatarGroup
                                    total={additionalCount}
                                    variant="circular"
                                    sx={{
                                      zIndex: "1",
                                    }}
                                  ></AvatarGroup>
                                </>
                              );
                            })()}
                          </div>
                        </TableCell>
                        <TableCell align="left">
                          <>{formatDateToDDMMYY(row?.last_updated)}</>
                        </TableCell>
                        <TableCell align="left">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Avatar
                              src="/broken-image.jpg"
                              sx={{ width: 24, height: 24 }}
                            />
                            <Typography variant="body2">
                              <>
                                {(() => {
                                  const ownerId = Number(row?.owners?.[0]);
                                  const ownerUser = allUsers.find(
                                    (user: any) => user.id === ownerId,
                                  );
                                  return ownerUser
                                    ? `${ownerUser.first_name} ${ownerUser.last_name}`
                                    : " ";
                                })()}
                              </>
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="left">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {(() => {
                              const assignees = row?.assignees || [];
                              const assigneeUsers = assignees
                                .map((id: number) =>
                                  allUsers.find((user: any) => user.id === id),
                                )
                                .filter(Boolean);

                              if (assigneeUsers.length === 0) {
                                return <Typography>-</Typography>;
                              } else if (assigneeUsers.length === 1) {
                                // Single assignee: show avatar and full name
                                const user = assigneeUsers[0];
                                const fullName = `${user.first_name} ${user.last_name}`;
                                return (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        fontSize: "0.75rem",
                                      }}
                                    >
                                      {user.first_name.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Typography variant="body2">
                                      {fullName}
                                    </Typography>
                                  </Box>
                                );
                              } else {
                                // Multiple assignees: show avatars only
                                const displayUsers = assigneeUsers.slice(0, 4);
                                const remainingCount = Math.max(
                                  0,
                                  assigneeUsers.length - 4,
                                );

                                return (
                                  <AvatarGroup
                                    max={5}
                                    sx={{
                                      "& .MuiAvatar-root": {
                                        width: 24,
                                        height: 24,
                                        fontSize: "0.75rem",
                                      },
                                    }}
                                  >
                                    {displayUsers.map(
                                      (user: any, index: number) => {
                                        const fullName = `${user.first_name} ${user.last_name}`;
                                        return (
                                          <Tooltip key={index} title={fullName}>
                                            <Avatar
                                              sx={{
                                                width: 24,
                                                height: 24,
                                                fontSize: "0.75rem",
                                              }}
                                            >
                                              {user.first_name
                                                .charAt(0)
                                                .toUpperCase()}
                                            </Avatar>
                                          </Tooltip>
                                        );
                                      },
                                    )}
                                    {remainingCount > 0 && (
                                      <Avatar sx={{ width: 24, height: 24 }}>
                                        +{remainingCount}
                                      </Avatar>
                                    )}
                                  </AvatarGroup>
                                );
                              }
                            })()}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                <TableRow>
                  <TableCell align="left">
                    <strong>Total:</strong>
                  </TableCell>
                  <TableCell align="center"></TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        fontWeight: totalCount > 10 ? "bold" : "normal",
                        color: totalCount > 10 ? "#D32F2F" : "inherit",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      {totalCount}
                    </Box>
                  </TableCell>
                  {displayMode === "Extended" && (
                    <TableCell colSpan={10}></TableCell>
                  )}
                  {displayMode === "Classic" && (
                    <TableCell colSpan={5}></TableCell>
                  )}
                  {!displayMode && <TableCell colSpan={5}></TableCell>}
                </TableRow>
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  <Typography variant="body1" color="textSecondary">
                    No Data Available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack
        spacing={1}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={filteredData?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
              {
                display: "none",
              },
            ".MuiTablePagination-actions": {
              display: "none",
            },
            ".MuiTablePagination-toolbar": {
              paddingLeft: "16px",
            },
          }}
        />
        <Pagination
          count={Math.ceil(filteredData?.length / rowsPerPage)}
          showFirstButton
          showLastButton
          className="pagination-button"
          color="secondary"
          size="small"
          sx={{
            ".css-wjh20t-MuiPagination-ul": {
              justifyContent: "center",
            },
          }}
          onChange={(_, value) => handleChangePage(null, value - 1)}
          page={page + 1}
        />
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredData?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          classes={{
            displayedRows: "hidden-displayed-rows",
          }}
          sx={{
            ".MuiTablePagination-actions": {
              display: "none",
            },
            ".MuiTablePagination-input": {
              marginRight: "15px",
            },
          }}
        />
      </Stack>
    </>
  );
}
export default DataAccuracyTable;
